import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useTheme } from '@cbhq/cds-web/hooks/useTheme';
import { useGetApplicationsV2 } from '@cbhq/identity-api-hooks/app-switcher/fetch';

import { PACKAGE_VERSION } from '../environment';
import { useCacheIllustrations } from '../hooks/useCacheIllustrations';
import {
  mapApiApplicationsToApplications,
  mapApiCategoriesToCategories,
  mapApiSpotLightToSpotLight,
} from '../utils/api-utils';

import { Application, Category, SpotLight } from './Applications.types';
import { useClientProps } from './ClientPropsProvider';

type ApplicationsContextType = {
  categories: Category[];
  applications: Application[];
  spotLight?: SpotLight;
};
type ApplicationsProviderFunction = () => ApplicationsContextType;

export const ApplicationsContext = createContext<ApplicationsProviderFunction>(() => {
  throw new Error('ApplicationsContext used before being provided');
});

type ApplicationsProviderProps = { children: ReactNode };

export function ApplicationsProvider(props: ApplicationsProviderProps) {
  const { locale } = useIntl();

  const clientProps = useClientProps();
  const applicationsApi = useGetApplicationsV2({
    clientOptions: {
      projectId: clientProps.projectId,
      action: clientProps.action,
      locale,
      uiVersion: PACKAGE_VERSION,
    },
  });

  const { activeColorScheme: spectrum } = useTheme();

  const applications = useMemo(
    () =>
      applicationsApi.applications?.map((app) => mapApiApplicationsToApplications(app, spectrum)) ??
      [],
    [applicationsApi.applications, spectrum],
  );

  const categories = useMemo(
    () => applicationsApi.categories?.map(mapApiCategoriesToCategories) ?? [],
    [applicationsApi.categories],
  );

  useCacheIllustrations(spectrum, applications);

  const value: ApplicationsProviderFunction = useCallback(
    () => ({
      categories,
      applications,
      spotLight: applicationsApi.spotlight
        ? mapApiSpotLightToSpotLight({ spotLight: applicationsApi.spotlight, spectrum })
        : undefined,
    }),
    [applications, categories, applicationsApi?.spotlight, spectrum],
  );

  return <ApplicationsContext.Provider value={value} {...props} />;
}

export function useApplicationsContext() {
  const { applications } = useContext(ApplicationsContext)();
  return applications;
}

export function useCategoriesContext() {
  const { categories } = useContext(ApplicationsContext)();
  return categories;
}

export function useSpotLightContext() {
  const { spotLight } = useContext(ApplicationsContext)();
  return spotLight;
}
